import Image from "next/image";
import Link from "next/link";
import { sanitizeHTML } from "@/lib/utils";
import { getReadableImageUrl } from "@/lib/directus";

interface AdditionalProcedureLinksData {
  heading?: {
    text?: string;
    title?: string;
  };
  text?: {
    text?: string;
  };
  background_video?: {
    id: string;
    filename_download: string;
  };
  procedure_links?: {
    menu_items_id: {
      href: string;
      label: string;
    };
  }[];
}

export const fields = [
  // heading
  "heading.text",
  "heading.title",

  // text
  "text.text",

  // background video
  "background_video.id",
  "background_video.filename_download",

  // procedure links
  "procedure_links.menu_items_id.href",
  "procedure_links.menu_items_id.label",
] as const;

interface AdditionalProcedureLinksProps {
  data?: AdditionalProcedureLinksData;
}

export default function AdditionalProcedureLinks({ data }: AdditionalProcedureLinksProps) {
  return (
    <section className="overflow-hidden w-full"> 
      <div className="h-full overflow-hidden relative w-full px-8 py-8 xl:py-32">
        <div className="absolute inset-0 z-10">
          {data?.background_video && (
            <video
              autoPlay
              muted
              loop
              playsInline
              className="absolute z-10 inset-0 w-full h-full object-cover"
            >
              <source 
                src={getReadableImageUrl(data.background_video.id, data.background_video.filename_download)} 
                type="video/mp4" 
              />
            </video>
          )}

          <div className="absolute inset-0 bg-primary/50 xl:bg-primary/30 z-20" />
        </div>

        {/* Content Container */}
        <div className="relative z-20 h-full gl-container flex items-center justify-center">
          <div>
            <div className="xl:bg-white/70 xl:rounded-2xl xl:p-16 xl:max-w-[85%] xl:mx-auto gl-desktop-scale">
              {/* Heading */}
              <h2 className="text-[2.375rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-white xl:text-primary xl:text-center mb-6">
                {data?.heading?.title}
              </h2>

              {/* Description */}
              <div 
                className="text-sm xl:text-xl text-white xl:text-primary font-greycliff leading-tight tracking-tight mb-6 xl:text-center xl:mb-12 max-w-[50rem] mx-auto [&>*]:text-[1rem] [&>*]:font-normal [&>*]:leading-normal xl:[&>*]:text-xl"
                dangerouslySetInnerHTML={sanitizeHTML(data?.text?.text || "")}
              />

              {/* Procedures Grid */}
              <div className="flex flex-wrap gap-2 xl:gap-4 xl:justify-center text-sm">
                {data?.procedure_links?.map((link, index) => (
                  <Link 
                    key={index} 
                    href={link.menu_items_id.href || "#"} 
                    className="px-4 py-2 xl:px-6 xl:py-3 rounded-full border font-greycliff text-lg xl:text-2xl flex items-center border-white xl:border-primary text-white xl:text-primary transition-colors hover:bg-[#dac79b] hover:text-primary hover:border-transparent active:bg-[#dac79b] active:text-primary active:border-transparent"
                  >
                    {link.menu_items_id.label}
                    <Image 
                      src="/images/arrow.svg" 
                      alt="Arrow" 
                      width={26}
                      quality={90}
                      height={27} 
                      className="ml-2 inline-block [filter:brightness(0)_invert(1)] xl:filter-none" 
                    />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
